import type { ReactNode } from "react";
import { Fretboard, type LitPosition } from "@music-app/ui-components";
import {
  STANDARD_TUNING,
  midiAt,
  pitchFromMidi,
  pitchToSolfege,
  type FretPosition,
} from "@music-app/theory-engine";
import type { FretboardGrading } from "@music-app/exercise-generator";
import type { ExerciseRendererProps } from "./types.js";

const samePos = (a: FretPosition, b: FretPosition): boolean =>
  a.stringIndex === b.stringIndex && a.fret === b.fret;

function solfegeFor(midi: number, tonicMidi: number): string {
  return pitchToSolfege(pitchFromMidi(midi, false), pitchFromMidi(tonicMidi, false), false);
}

export function FretboardRenderer({
  exercise,
  pendingAnswer,
  gradingResult,
  disabled,
  onAnswerChange,
  onSubmit,
}: ExerciseRendererProps): ReactNode {
  const fb = exercise.meta.fretboard;
  if (!fb) return null;

  const taps: FretPosition[] = pendingAnswer?.type === "fretboard" ? pendingAnswer.taps : [];
  const grading = gradingResult?.detail as FretboardGrading | null | undefined;
  const isGraded = gradingResult !== null;
  const isMultiTap = fb.notesToPlace > 1;
  const tonicMidi = exercise.meta.tonicMidi ?? null;

  const labelFor = (pos: FretPosition): string | undefined =>
    isMultiTap || tonicMidi === null
      ? undefined
      : solfegeFor(midiAt(STANDARD_TUNING, pos), tonicMidi);

  const lit: LitPosition[] = [];
  for (const g of fb.given) lit.push({ pos: g, role: "given", label: labelFor(g) });

  if (!isGraded) {
    for (const t of taps) lit.push({ pos: t, role: "selected", label: labelFor(t) });
  } else if (grading) {
    taps.forEach((pos, i) => {
      const st = grading.perTap[i];
      const role = st === "correct" ? "correct" : st === "duplicate" ? "duplicate" : "wrong";
      lit.push({ pos, role, label: labelFor(pos) });
    });
    if (!grading.correct) {
      for (const s of fb.solution) {
        if (!taps.some((p) => samePos(p, s))) {
          lit.push({ pos: s, role: "solution", label: labelFor(s) });
        }
      }
    }
  }

  const handleTap = (pos: FretPosition) => {
    if (disabled || isGraded) return;
    const exists = taps.some((t) => samePos(t, pos));
    const newTaps = exists ? taps.filter((t) => !samePos(t, pos)) : [...taps, pos];
    const answer = { type: "fretboard" as const, taps: newTaps };
    onAnswerChange(answer);
    if (!isMultiTap && newTaps.length === fb.notesToPlace) {
      onSubmit(answer);
    }
  };

  const ariaLabel = isMultiTap
    ? "Fretboard — build the chord you heard on the highlighted strings"
    : "Fretboard — tap the note you heard above the root";

  const canCheck = isMultiTap && !isGraded && taps.length === fb.notesToPlace;

  return (
    <div>
      <Fretboard
        tuning={STANDARD_TUNING}
        litPositions={lit}
        allowedStrings={fb.allowedStrings}
        onTap={!isGraded ? handleTap : undefined}
        disabled={disabled}
        ariaLabel={ariaLabel}
        noteLabel={isMultiTap ? "letter" : "solfege"}
      />
      {canCheck && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => onSubmit({ type: "fretboard", taps })}
            className="bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors"
          >
            Check
          </button>
        </div>
      )}
    </div>
  );
}
